import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { useReactToPrint } from 'react-to-print';
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Tooltip, Legend, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Cell } from 'recharts';
import API_BASE_URL from './config';
import './css/LeaderFeedbackVisualization.css';

const COMPASS_CATEGORIES = [
  { name: 'Continual Learning', questionIndices: [0, 1, 2] }, { name: 'Awareness', questionIndices: [3, 4, 5] },
  { name: 'Magic Dust', questionIndices: [6, 7, 8] }, { name: 'VMGM=B', questionIndices: [9, 10, 11] },
  { name: 'Communications', questionIndices: [12, 13, 14] }, { name: 'Clarity', questionIndices: [15, 16, 17] },
  { name: 'Accountability', questionIndices: [18, 19, 20] }, { name: 'Conflict', questionIndices: [21, 22, 23] },
  { name: 'Influence', questionIndices: [24, 25, 26] }, { name: 'Relationships', questionIndices: [27, 28, 29] },
  { name: 'Feedback', questionIndices: [30, 31, 32] }, { name: 'Inspiration', questionIndices: [33, 34, 35] },
];

const BALANCING_SKILLS = [
    "Exhibits Curiosity", "Assesses Own Skills", "Learns New Approaches", "Demonstrates Humility", "Considers Other Perspectives", "Confirms Own Role",
    "Examines Own Essence", "Receptive to Feedback", "Develops Others", "Creates a Shared Vision", "Identifies New Challenges", "Aligns Behaviors",
    "Is Approachable", "Develops Others", "Facilitates Dialog", "Eliminates Obscurities", "Validates Required Actions", "Aligns Roles to Goals",
    "Demonstrates Integrity", "Develops Others", "Creates Accountability Culture", "Demonstrates Empathy", "Facilitates Resolution", "Identifies New Opportunities",
    "Rallies with Enthusiasm", "Shows Confidence", "Maintains Composure", "Continuously Expands Network", "Shows Authentic Self", "Creates Culture of Respect",
    "Receptive to Feedback", "Champions Improvements", "Develops Others", "Rallies with Enthusiasm", "Energizes and Excites", "Demonstrates Loyalty"
];

const QUESTION_TEXTS = [
    "Proactively seeks to learn new skills to keep current with changing times", "Recognizes when he or she has a skill gap and seeks to learn", "Collaborates and networks to learn new approaches",
    "Is aware of, or open to suggestions about his/her limitations", "Considers points of view other than his or her own", "Understands the role he or she plays on the team and in various situations",
    "Is aware of his or her own unique strengths and uses them to accomplish tasks", "Takes feedback to know when he or she is overusing a strength", "Recognizes and leverages the strengths of others as appropriate",
    "Establishes a vision and a mission that sets the bar high", "Introduces new challenges for the team with a clear vision", "Behaves in a manner that is consistent with the vision and mission",
    "Is welcoming and approachable", "Is quick to give others recognition when deserved", "Effectively listens while encouraging people to share thoughts",
    "Presents a plan that is clear, concise and achievable", "Presents a plan that provides the actions that are needed", "Presents a plan with clearly defined roles",
    "Holds himself or herself accountable for mistakes as necessary and appropriate", "Holds others accountable for mistakes when necessary and appropriate", "Takes responsibility and promotes others to do the same",
    "Shows empathy and listens to others during conflict", "Challenges the group to address issues when there are strong disagreements", "He or she promotes opportunities for improvement when there is conflict",
    "Influences others while being respectful, positive and enthusiastic", "Presents his/her self confidently", "Provides a calming influence in a heated situation",
    "Effectively reaches out to others beyond the immediate group", "This person is open and authentic", "This person helps foster a mutual respect",
    "Gives extra effort to seek out input from others for self-improvement", "Actively seeks input from the group to improve products or processes", "Encourages others to seek feedback as part of their development",
    "Effectively inspires others to stretch beyond their comfort zones", "Presents new ideas in a way that energizes and excites", "Individuals feel valued and inspired by this person"
];

const average = (arr) => {
  const validNumbers = arr.filter(v => typeof v === 'number' && !isNaN(v));
  if (validNumbers.length === 0) return null;
  return validNumbers.reduce((a, b) => a + b, 0) / validNumbers.length;
};

const analyzeThemes = (comments) => {
    const stopWords = new Set(['i', 'me', 'my', 'myself', 'we', 'our', 'ours', 'ourselves', 'you', 'your', 'yours', 'yourself', 'yourselves', 'he', 'him', 'his', 'himself', 'she', 'her', 'hers', 'herself', 'it', 'its', 'itself', 'they', 'them', 'their', 'theirs', 'themselves', 'what', 'which', 'who', 'whom', 'this', 'that', 'these', 'those', 'am', 'is', 'are', 'was', 'were', 'be', 'been', 'being', 'have', 'has', 'had', 'having', 'do', 'does', 'did', 'doing', 'a', 'an', 'the', 'and', 'but', 'if', 'or', 'because', 'as', 'until', 'while', 'of', 'at', 'by', 'for', 'with', 'about', 'against', 'between', 'into', 'through', 'during', 'before', 'after', 'above', 'below', 'to', 'from', 'up', 'down', 'in', 'out', 'on', 'off', 'over', 'under', 'again', 'further', 'then', 'once', 'here', 'there', 'when', 'where', 'why', 'how', 'all', 'any', 'both', 'each', 'few', 'more', 'most', 'other', 'some', 'such', 'no', 'nor', 'not', 'only', 'own', 'same', 'so', 'than', 'too', 'very', 's', 't', 'can', 'will', 'just', 'don', 'should', 'now', 'person', 'individual', 'leader', 'team', 'would', 'also']);
    const wordCounts = {};
    const allText = comments.join(' ').toLowerCase().replace(/[.,!?;:()]/g, '');
    
    allText.split(/\s+/).forEach(word => {
        if (word && !stopWords.has(word)) {
            wordCounts[word] = (wordCounts[word] || 0) + 1;
        }
    });

    return Object.entries(wordCounts)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 10)
        .map(([text, count]) => ({ text, count }));
};

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="custom-tooltip">
        <p className="label">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} style={{ color: entry.color }}>
            {`${entry.name}: ${entry.value != null ? parseFloat(entry.value).toFixed(2) : 'N/A'}`}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

const LeaderFeedbackVisualization = ({ customerIdForReport }) => {
  const { customerId: customerIdFromParams } = useParams();
  const customerId = customerIdForReport || customerIdFromParams;
  const componentRef = useRef();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [formResponses, setFormResponses] = useState([]);
  const [isSelfService, setIsSelfService] = useState(false);
  const [benchmarkData, setBenchmarkData] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!customerId) {
        setError("Could not find a Customer ID to generate the report.");
        setLoading(false);
        return;
      }
      const isAdminRoute = window.location.pathname.includes('/leader-visual-report/');
      const apiUrl = isAdminRoute
        ? `${API_BASE_URL}/api/customer-visual-report/${customerId}`
        : `${API_BASE_URL}/api/my-report/${customerId}`;
      setIsSelfService(!isAdminRoute);
      try {
        const [reportRes, benchmarkRes] = await Promise.all([
          axios.get(apiUrl, { withCredentials: true }),
          axios.get(`${API_BASE_URL}/api/benchmark-data/${customerId}`, { withCredentials: true })
        ]);
        setCustomer(reportRes.data.data.customer);
        setFormResponses(reportRes.data.data.formResponses || []);
        setBenchmarkData(benchmarkRes.data);
      } catch (err) {
        setError('Failed to load report data: ' + (err.response?.data?.message || err.message));
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [customerId]);

  const processedData = useMemo(() => {
    if (formResponses.length === 0) return null;

    const responses = formResponses
      .filter(resp => resp?.questions?.length === 36)
      .map(resp => ({ ...resp, questions: resp.questions.map(q => (q > 0 ? q - 1 : null)) }));

    const selfResponse = responses.find(r => r.relationshipToSubject === 'Self') || null;
    const allRatersLessSelf = responses.filter(r => r.relationshipToSubject !== 'Self');
    
    const raterGroups = {
        Manager: allRatersLessSelf.filter(r => r.relationshipToSubject === 'Manager'),
        Peer: allRatersLessSelf.filter(r => r.relationshipToSubject === 'Peer'),
        'Direct Report': allRatersLessSelf.filter(r => r.relationshipToSubject === 'Direct Report'),
        Other: allRatersLessSelf.filter(r => r.relationshipToSubject === 'Other'),
    };

    const getCategoryAverages = (resps) => {
        if (!resps || resps.length === 0) return Array(12).fill(null);
        return COMPASS_CATEGORIES.map(cat => average(cat.questionIndices.flatMap(qIndex => resps.map(r => r.questions[qIndex]))));
    };
    
    const getSingleQuestionAverages = (qIndex, resps) => average(resps.map(r => r.questions[qIndex]));
    const selfAvg = selfResponse ? getCategoryAverages([selfResponse]) : Array(12).fill(null);
    const allRatersLessSelfAvg = getCategoryAverages(allRatersLessSelf);
    const managerAvg = getCategoryAverages(raterGroups.Manager);
    const peerAvg = getCategoryAverages(raterGroups.Peer);
    const directReportAvg = getCategoryAverages(raterGroups['Direct Report']);
    const otherAvg = getCategoryAverages(raterGroups.Other);

    const reportStrings = () => {
        const sortedBehaviors = COMPASS_CATEGORIES
            .map((cat, i) => ({ name: cat.name, score: allRatersLessSelfAvg[i] }))
            .filter(item => item.score !== null)
            .sort((a, b) => b.score - a.score);
        const strengths = sortedBehaviors.slice(0, 3).map(item => `${item.name.replace(/\\n/g, '')}`);
        const improvements = sortedBehaviors.slice(-3).reverse().map(item => `${item.name.replace(/\\n/g, '')}`);
        let selfVsRatersMore = 0, selfVsRatersLess = 0;
        if (selfAvg.every(s => s !== null)) {
            for (let i = 0; i < 12; i++) {
                if (allRatersLessSelfAvg[i] > selfAvg[i]) selfVsRatersMore++;
                if (allRatersLessSelfAvg[i] < selfAvg[i]) selfVsRatersLess++;
            }
        }
        const radarTwoCommentary = `The dotted line represents your self-ratings. Your raters frequently saw you exhibiting stronger leadership behaviors than you did in ${selfVsRatersMore} compass areas. Your raters saw you exhibiting the behaviors less often than you in ${selfVsRatersLess} compass areas. Please review and note what is the same and what is different.`;
        return { strengths, improvements, radarTwoCommentary };
    };
    
    const allComments = responses.flatMap(r => [r.strengths, r.doDifferently, r.additionalComments]).filter(Boolean);
    const commentThemes = analyzeThemes(allComments);

    return {
        selfResponse, allRatersLessSelf, raterGroups, selfAvg, allRatersLessSelfAvg, managerAvg,
        peerAvg, directReportAvg, otherAvg, getSingleQuestionAverages, reportStrings: reportStrings(),
        raterCounts: { M: raterGroups.Manager.length, P: raterGroups.Peer.length, DR: raterGroups['Direct Report'].length, O: raterGroups.Other.length },
        writtenFeedback: formResponses.map(r => ({
            relationship: r.relationshipToSubject,
            strengths: r.strengths || "N/A",
            doDifferently: r.doDifferently || "N/A",
            additionalComments: r.additionalComments || "N/A"
        })),
        commentThemes
    };
  }, [formResponses]);

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    documentTitle: `PTEK_REPORT_${customer?.firstName}_${customer?.lastName}`,
  });

  if (loading) return <div className="container loading">Loading Report Data...</div>;
  if (error) return <div className="container error">Error: {error}</div>;
  if (!customer || !processedData || !benchmarkData) return <div className="container">No data available to generate a report.</div>;

  const { selfAvg, allRatersLessSelfAvg, managerAvg, peerAvg, directReportAvg, otherAvg, raterCounts, getSingleQuestionAverages, reportStrings, writtenFeedback, selfResponse, allRatersLessSelf, raterGroups, commentThemes } = processedData;

  const radarData = COMPASS_CATEGORIES.map((cat, i) => {
    const orgAvg = benchmarkData.organizationAverages[cat.name] ? parseFloat(benchmarkData.organizationAverages[cat.name]) - 1 : null;
    const globalAvg = benchmarkData.globalAverages[cat.name] ? parseFloat(benchmarkData.globalAverages[cat.name]) - 1 : null;
    return {
      category: cat.name.replace(/ /g, '\n'),
      'Self': selfAvg[i],
      'All Raters': allRatersLessSelfAvg[i],
      'Manager': managerAvg[i],
      'Peer': peerAvg[i],
      'Direct Report': directReportAvg[i],
      'Other': otherAvg[i],
      'My Organization Avg.': orgAvg,
      'Global Avg.': globalAvg,
    };
  });
  
  const highLightsData = COMPASS_CATEGORIES.map((cat, i) => ({
      name: cat.name,
      'All Users Rating': allRatersLessSelfAvg[i],
  }));

  const formatAverage = (val) => val !== null ? parseFloat(val).toFixed(1) : 'NR';
    
  const getBarColor = (score) => {
    if (score === null) return 'var(--color-nodata)';
    if (score >= 4.5) return 'var(--color-high)';
    if (score >= 4.0) return 'var(--color-medium)';
    return 'var(--color-low)';
  };
  
  return (
    <div className="report-body">
      <div className="container" ref={componentRef}>
        {!isSelfService && (
            <div className="pdf-export no-print">
                <button className="pdf-button" onClick={handlePrint}>Export to PDF</button>
            </div>
        )}
        <section className="section page-break">
            <div className="header">
                <h1>Peopletek 360° COMPASS</h1>
                <h2>FEEDBACK REPORT</h2>
                <div className="prepared-for">
                    <p>PREPARED FOR</p>
                    <h3>{customer.firstName} {customer.lastName}</h3>
                    <p>{customer.organization}</p>
                    <p>{new Date().toLocaleDateString()}</p>
                </div>
            </div>
        </section>
        <section className="section page-break">
            <h3 className="sectionHeading">Compass Snapshot with Benchmarks</h3>
            <div className="chartWrapper">
                <ResponsiveContainer width="100%" height={500}>
                    <RadarChart cx="50%" cy="50%" outerRadius="80%" data={radarData}>
                        <PolarGrid />
                        <PolarAngleAxis dataKey="category" tick={{ fontSize: 12 }} />
                        <PolarRadiusAxis angle={30} domain={[0, 5]} tickCount={6}/>
                        <Tooltip content={<CustomTooltip />} />
                        <Legend />
                        <Radar name="All Raters" dataKey="All Raters" stroke="var(--color-all-raters)" fill="var(--color-all-raters)" fillOpacity={0.6} />
                        <Radar name="My Organization Avg." dataKey="My Organization Avg." stroke="#ff7300" fill="transparent" strokeDasharray="3 3" strokeWidth={2}/>
                        <Radar name="Global Avg." dataKey="Global Avg." stroke="#38761d" fill="transparent" strokeDasharray="1 4" strokeWidth={2}/>
                    </RadarChart>
                </ResponsiveContainer>
            </div>
            <p className="commentary">This chart shows your scores from all raters (in blue) compared to the average scores of other leaders within your organization and across all organizations. This provides context for where you excel and where you have opportunities for growth relative to your peers.</p>
        </section>
        <section className="section page-break">
            <h3 className="sectionHeading">Self-Ratings vs. All Raters</h3>
             <div className="chartWrapper">
                <ResponsiveContainer width="100%" height={500}>
                    <RadarChart cx="50%" cy="50%" outerRadius="80%" data={radarData}>
                        <PolarGrid />
                        <PolarAngleAxis dataKey="category" tick={{ fontSize: 12 }}/>
                        <PolarRadiusAxis angle={30} domain={[0, 5]} tickCount={6}/>
                        <Tooltip content={<CustomTooltip />} />
                        <Legend />
                        <Radar name="Self" dataKey="Self" stroke="var(--color-self)" strokeDasharray="5 5" fill="transparent" />
                        <Radar name="All Raters" dataKey="All Raters" stroke="var(--color-all-raters)" fill="var(--color-all-raters)" fillOpacity={0.6} />
                    </RadarChart>
                </ResponsiveContainer>
            </div>
            <p className="commentary">{reportStrings.radarTwoCommentary}</p>
        </section>
        <section className="section page-break">
            <h3 className="sectionHeading">Comparison by Relationship Group</h3>
            <div className="radarGrid">
                <div className="radar-item">
                    <h4>Self vs. Manager ({raterCounts.M})</h4>
                    <ResponsiveContainer width="100%" height={300}>
                        <RadarChart data={radarData}>
                            <PolarGrid /><PolarAngleAxis dataKey="category" tick={{ fontSize: 10 }}/><PolarRadiusAxis domain={[0, 5]} tickCount={6}/><Tooltip content={<CustomTooltip />}/>
                            <Radar name="Self" dataKey="Self" stroke="var(--color-self)" strokeDasharray="5 5" fill="transparent" />
                            <Radar name="Manager" dataKey="Manager" stroke="var(--color-manager)" fill="var(--color-manager)" fillOpacity={0.5} />
                        </RadarChart>
                    </ResponsiveContainer>
                </div>
                <div className="radar-item">
                    <h4>Self vs. Peer ({raterCounts.P})</h4>
                    <ResponsiveContainer width="100%" height={300}>
                        <RadarChart data={radarData}>
                            <PolarGrid /><PolarAngleAxis dataKey="category" tick={{ fontSize: 10 }}/><PolarRadiusAxis domain={[0, 5]} tickCount={6}/><Tooltip content={<CustomTooltip />}/>
                            <Radar name="Self" dataKey="Self" stroke="var(--color-self)" strokeDasharray="5 5" fill="transparent" />
                            <Radar name="Peer" dataKey="Peer" stroke="var(--color-peer)" fill="var(--color-peer)" fillOpacity={0.5} />
                        </RadarChart>
                    </ResponsiveContainer>
                </div>
                <div className="radar-item">
                    <h4>Self vs. Direct Report ({raterCounts.DR})</h4>
                    <ResponsiveContainer width="100%" height={300}>
                        <RadarChart data={radarData}>
                            <PolarGrid /><PolarAngleAxis dataKey="category" tick={{ fontSize: 10 }}/><PolarRadiusAxis domain={[0, 5]} tickCount={6}/><Tooltip content={<CustomTooltip />}/>
                            <Radar name="Self" dataKey="Self" stroke="var(--color-self)" strokeDasharray="5 5" fill="transparent" />
                            <Radar name="Direct Report" dataKey="Direct Report" stroke="var(--color-direct-report)" fill="var(--color-direct-report)" fillOpacity={0.5} />
                        </RadarChart>
                    </ResponsiveContainer>
                </div>
                <div className="radar-item">
                    <h4>Self vs. Other ({raterCounts.O})</h4>
                    <ResponsiveContainer width="100%" height={300}>
                        <RadarChart data={radarData}>
                            <PolarGrid /><PolarAngleAxis dataKey="category" tick={{ fontSize: 10 }}/><PolarRadiusAxis domain={[0, 5]} tickCount={6}/><Tooltip content={<CustomTooltip />}/>
                            <Radar name="Self" dataKey="Self" stroke="var(--color-self)" strokeDasharray="5 5" fill="transparent" />
                            <Radar name="Other" dataKey="Other" stroke="var(--color-other)" fill="var(--color-other)" fillOpacity={0.5} />
                        </RadarChart>
                    </ResponsiveContainer>
                </div>
            </div>
        </section>
        <section className="section page-break">
            <h3 className="sectionHeading">Leadership Behaviors High-Lights</h3>
            <div className="chartWrapper">
                <ResponsiveContainer width="100%" height={500}>
                    <BarChart data={highLightsData} margin={{ top: 20, right: 30, left: 20, bottom: 120 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" angle={-45} textAnchor="end" interval={0} height={120} tick={{fontSize: 12}}/>
                        <YAxis type="number" domain={[0, 5]} ticks={[0, 0.5, 1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5]} />
                        <Tooltip content={<CustomTooltip />} />
                        <Bar dataKey="All Users Rating" name="All Raters Average">
                            {highLightsData.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={getBarColor(entry['All Users Rating'])} />
                            ))}
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            </div>
            <div className="summary-grid" style={{marginTop: '2rem'}}>
                <div className="summary-card strengths">
                    <h4>Strongest Leadership Behaviors</h4>
                    {reportStrings.strengths.map((s, i) => <p key={i}>{s}</p>)}
                </div>
                <div className="summary-card improvements">
                    <h4>Opportunities for Improvement</h4>
                    {reportStrings.improvements.map((imp, i) => <p key={i}>{imp}</p>)}
                </div>
            </div>
        </section>
        <section className="section page-break">
            <h3 className="sectionHeading">Detailed Scores: Part 1</h3>
            <table className="feedbackTable">
                <thead>
                    <tr>
                        <th>Compass Behaviors</th><th>Balancing Skills</th>
                        <th className="numeric-cell">Self</th>
                        <th className="numeric-cell">All Raters</th>
                        <th className="numeric-cell">Manager</th>
                        <th className="numeric-cell">Peer</th>
                        <th className="numeric-cell">Direct Report</th>
                        <th className="numeric-cell">Other</th>
                    </tr>
                </thead>
                <tbody>
                    {COMPASS_CATEGORIES.slice(0, 6).map((cat, catIdx) => (
                        <React.Fragment key={cat.name}>
                            <tr className="categoryHeaderRow">
                                <td>{cat.name}</td><td>Category Average</td>
                                <td className="numeric-cell">{formatAverage(selfAvg[catIdx])}</td>
                                <td className="numeric-cell">{formatAverage(allRatersLessSelfAvg[catIdx])}</td>
                                <td className="numeric-cell">{formatAverage(managerAvg[catIdx])}</td>
                                <td className="numeric-cell">{formatAverage(peerAvg[catIdx])}</td>
                                <td className="numeric-cell">{formatAverage(directReportAvg[catIdx])}</td>
                                <td className="numeric-cell">{formatAverage(otherAvg[catIdx])}</td>
                            </tr>
                            {cat.questionIndices.map((qIndex) => (
                                <tr key={qIndex}>
                                    <td>{QUESTION_TEXTS[qIndex]}</td><td>{BALANCING_SKILLS[qIndex]}</td>
                                    <td className="numeric-cell">{formatAverage(selfResponse?.questions[qIndex])}</td>
                                    <td className="numeric-cell">{formatAverage(getSingleQuestionAverages(qIndex, allRatersLessSelf))}</td>
                                    <td className="numeric-cell">{formatAverage(getSingleQuestionAverages(qIndex, raterGroups.Manager))}</td>
                                    <td className="numeric-cell">{formatAverage(getSingleQuestionAverages(qIndex, raterGroups.Peer))}</td>
                                    <td className="numeric-cell">{formatAverage(getSingleQuestionAverages(qIndex, raterGroups['Direct Report']))}</td>
                                    <td className="numeric-cell">{formatAverage(getSingleQuestionAverages(qIndex, raterGroups.Other))}</td>
                                </tr>
                            ))}
                        </React.Fragment>
                    ))}
                </tbody>
            </table>
        </section>
        <section className="section page-break">
            <h3 className="sectionHeading">Detailed Scores: Part 2</h3>
            <table className="feedbackTable">
                <thead>
                    <tr>
                        <th>Compass Behaviors</th><th>Balancing Skills</th>
                        <th className="numeric-cell">Self</th>
                        <th className="numeric-cell">All Raters</th>
                        <th className="numeric-cell">Manager</th>
                        <th className="numeric-cell">Peer</th>
                        <th className="numeric-cell">Direct Report</th>
                        <th className="numeric-cell">Other</th>
                    </tr>
                </thead>
                <tbody>
                    {COMPASS_CATEGORIES.slice(6, 12).map((cat, catIdx) => (
                        <React.Fragment key={cat.name}>
                            <tr className="categoryHeaderRow">
                                <td>{cat.name}</td><td>Category Average</td>
                                <td className="numeric-cell">{formatAverage(selfAvg[catIdx + 6])}</td>
                                <td className="numeric-cell">{formatAverage(allRatersLessSelfAvg[catIdx + 6])}</td>
                                <td className="numeric-cell">{formatAverage(managerAvg[catIdx + 6])}</td>
                                <td className="numeric-cell">{formatAverage(peerAvg[catIdx + 6])}</td>
                                <td className="numeric-cell">{formatAverage(directReportAvg[catIdx + 6])}</td>
                                <td className="numeric-cell">{formatAverage(otherAvg[catIdx + 6])}</td>
                            </tr>
                            {cat.questionIndices.map((qIndex) => (
                                <tr key={qIndex}>
                                    <td>{QUESTION_TEXTS[qIndex]}</td><td>{BALANCING_SKILLS[qIndex]}</td>
                                    <td className="numeric-cell">{formatAverage(selfResponse?.questions[qIndex])}</td>
                                    <td className="numeric-cell">{formatAverage(getSingleQuestionAverages(qIndex, allRatersLessSelf))}</td>
                                    <td className="numeric-cell">{formatAverage(getSingleQuestionAverages(qIndex, raterGroups.Manager))}</td>
                                    <td className="numeric-cell">{formatAverage(getSingleQuestionAverages(qIndex, raterGroups.Peer))}</td>
                                    <td className="numeric-cell">{formatAverage(getSingleQuestionAverages(qIndex, raterGroups['Direct Report']))}</td>
                                    <td className="numeric-cell">{formatAverage(getSingleQuestionAverages(qIndex, raterGroups.Other))}</td>
                                </tr>
                            ))}
                        </React.Fragment>
                    ))}
                </tbody>
            </table>
        </section>
        {COMPASS_CATEGORIES.map((category) => (
            <section key={category.name} className="section page-break">
                <h3 className="sectionHeading">{category.name}</h3>
                <div className="chartWrapper">
                    <ResponsiveContainer width="100%" height={250}>
                        <BarChart
                            layout="vertical"
                            data={category.questionIndices.map(qIndex => ({
                                name: BALANCING_SKILLS[qIndex],
                                'All Raters': getSingleQuestionAverages(qIndex, allRatersLessSelf)
                            }))}
                            margin={{ top: 20, right: 30, left: 180, bottom: 5 }}
                        >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis type="number" domain={[0, 5]} ticks={[0, 1, 2, 3, 4, 5]} />
                            <YAxis type="category" dataKey="name" width={180} tick={{fontSize: 12}} />
                            <Tooltip content={<CustomTooltip />}/>
                            <Bar dataKey="All Raters" fill="var(--color-all-raters)">
                                {category.questionIndices.map((qIndex, cellIndex) => (
                                    <Cell key={`cell-${cellIndex}`} fill={getBarColor(getSingleQuestionAverages(qIndex, allRatersLessSelf))} />
                                ))}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </section>
        ))}
        <section className="section page-break">
            <h3 className="sectionHeading">Open-Ended Feedback</h3>
            <div className="summary-grid">
                <div className="summary-card">
                    <h4>Key Themes from Rater Comments</h4>
                    {commentThemes.length > 0 ? (
                        <ul>
                            {commentThemes.map(theme => (
                                <li key={theme.text}>{theme.text} ({theme.count} mentions)</li>
                            ))}
                        </ul>
                    ) : (
                        <p>No significant themes found in comments.</p>
                    )}
                </div>
            </div>
            <table className="feedbackTable written-feedback" style={{marginTop: '2rem'}}>
                <thead>
                    <tr>
                        <th>Relationship</th>
                        <th>Strengths</th>
                        <th>Areas for Improvement</th>
                        <th>Additional Comments</th>
                    </tr>
                </thead>
                <tbody>
                    {writtenFeedback.map((fb, index) => (
                        <tr key={index}>
                            <td>{fb.relationship}</td>
                            <td>{fb.strengths}</td>
                            <td>{fb.doDifferently}</td>
                            <td>{fb.additionalComments}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </section>
      </div>
    </div>
  );
};

export default LeaderFeedbackVisualization;